import { useState, useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import { debounce } from 'lodash'
import * as PropTypes from 'prop-types'

import TextField from '../../../FormLegacy/FormFieldsV2/TextField'
import InfiniteScroll from '../../../InfiniteScroll/InfiniteScroll'
import {
  getGroupMembers,
  selectStatusOfGroupMembersLoading,
  setLoadingGroupMembersState,
  selectStatusOfGroupMembersLoadingAll,
  selectGroupMembersById,
  banUserFromGroup,
  unbanUserFromGroup,
} from '../../../../reducers/Organisation/groupsReducer'

import ContactItem from './MemberItem'

const NeighborsWindow = ({
  groupId,
  handleCancel,
  groupMembers,
  isLoadingMembers,
  isLoadedAllMembers,
  getGroupMembers,
  setLoadingGroupMembersState,
  banUserFromGroup,
  unbanUserFromGroup,
}) => {
  const [isSubmiting, setSubmiting] = useState(false)
  const [search, setSearch] = useState('')
  const [prevSearch, setPrevSearch] = useState('')
  const timeout = useRef(null)

  const fetchMembers = async (skip, searchingQuery, isScrolling) => {
    const skipMore =
      groupMembers.length % 50 === 0
        ? groupMembers.length / 50
        : (groupMembers.length - (groupMembers.length % 50)) / 50

    if (typeof searchingQuery === 'undefined') {
      try {
        await getGroupMembers(typeof skip !== 'undefined' ? skip : skipMore + 1, groupId)
      } catch (err) {
        throw new Error(err)
      }
    } else if (searchingQuery !== '' && isScrolling) {
      try {
        await getGroupMembers(
          typeof skip !== 'undefined' ? skip : skipMore + 1,
          groupId,
          searchingQuery,
        )
      } catch (err) {
        throw new Error(err)
      }
    } else if (searchingQuery !== '' && !isScrolling) {
      try {
        await getGroupMembers(
          typeof skip !== 'undefined' ? skip : searchingQuery ? 1 : skipMore + 1,
          groupId,
          searchingQuery,
        )
      } catch (err) {
        throw new Error(err)
      }
    } else if (searchingQuery === '' && isScrolling) {
      try {
        await getGroupMembers(skipMore + 1, groupId)
      } catch (err) {
        throw new Error(err)
      }
    } else {
      try {
        await getGroupMembers(1, groupId)
      } catch (err) {
        throw new Error(err)
      }
    }
  }

  const debounceFn = debounce(fetchMembers, 1000)

  const onBanSelected = async (userId) => {
    setSubmiting(true)
    try {
      await banUserFromGroup(groupId, userId)
    } catch (err) {
      throw new Error(err)
    } finally {
      setSubmiting(false)
    }
  }

  const onUnbanSelected = async (userId) => {
    setSubmiting(true)
    try {
      await unbanUserFromGroup(groupId, userId)
    } catch (err) {
      throw new Error(err)
    } finally {
      setSubmiting(false)
    }
  }

  const closeAndClear = () => {
    setLoadingGroupMembersState(true)
    handleCancel()
  }

  useEffect(() => {
    if (prevSearch !== search) {
      window.addEventListener('keydown', debounceFn(1, search))
    }
    setPrevSearch(search)
    return () => {
      window.removeEventListener('keydown', debounceFn)
    }
  }, [search])

  useEffect(() => {
    return () => {
      if (timeout.current) clearTimeout(timeout.current)
    }
  }, [])

  useEffect(() => {
    fetchMembers(1, search)
  }, [groupId])

  return (
    <div className="neighbors-window">
      <div className="neighbors-window__container">
        <TextField
          maxLength="50"
          name={'title'}
          placeholder={'Search member...'}
          onChange={(e) => {
            setSearch(e.target.value)
            setLoadingGroupMembersState(true)
          }}
          value={search}
        />

        <div className={`members-list ${isSubmiting ? 'is-loading' : ''}`}>
          {isLoadingMembers ? (
            <div>Loading...</div>
          ) : groupMembers.length ? (
            <InfiniteScroll
              data={groupMembers}
              renderItem={(item) => (
                <ContactItem
                  data={item}
                  onBanUser={() => onBanSelected(item.id)}
                  onUnbanUser={() => onUnbanSelected(item.id)}
                />
              )}
              onEndReached={() => {
                fetchMembers(1, search, true)
              }}
              style={{ height: '100%', paddingRight: '12px' }}
              isLoadedAll={isLoadedAllMembers}
            />
          ) : (
            <div>No results</div>
          )}
        </div>

        <div className="columns">
          <div className="column is-8">
            <button
              type="submit"
              className={`button button-gray is-block ${isSubmiting ? 'is-loading' : ''}`}
              onClick={() => closeAndClear()}
            >
              {'Close'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

NeighborsWindow.propTypes = {
  handleCancel: PropTypes.func,
  groupMembers: PropTypes.array,
  isLoadedAllMembers: PropTypes.bool,
  setLoadingGroupMembersState: PropTypes.func,
  isLoadingMembers: PropTypes.bool,
  groupId: PropTypes.number,
  getGroupMembers: PropTypes.func,
  banUserFromGroup: PropTypes.func,
  unbanUserFromGroup: PropTypes.func,
}

export default connect(
  (state, ownProps) => {
    return {
      groupMembers: selectGroupMembersById(state)(ownProps.groupId),
      isLoadingMembers: selectStatusOfGroupMembersLoading(state),
      isLoadedAllMembers: selectStatusOfGroupMembersLoadingAll(state),
    }
  },
  {
    setLoadingGroupMembersState,
    getGroupMembers,
    banUserFromGroup,
    unbanUserFromGroup,
  },
)(NeighborsWindow)
